import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';
import Swal from 'sweetalert2';

import WysiwygEditor from '../../components/WysiwygEditor';
import {
  Trash2,
  CloudUpload,
  ArrowLeft,
  FileText
} from 'lucide-react';

// Use API URL base (without /api) for assets, or fallback to localhost:5555
const getAssetBaseUrl = () => {
  if (process.env.REACT_APP_ASSET_BASE_URL) {
    return process.env.REACT_APP_ASSET_BASE_URL;
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  return 'http://localhost:5555';
};
const ASSET_BASE_URL = getAssetBaseUrl();

function IndemnityLandingPageEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [selectedFormId, setSelectedFormId] = useState('');
  const [disclaimerText, setDisclaimerText] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [waiverImageFile, setWaiverImageFile] = useState(null);
  const [waiverImagePreview, setWaiverImagePreview] = useState(null);
  const [waiverFileName, setWaiverFileName] = useState(null);
  const [uploadingWaiver, setUploadingWaiver] = useState(false);
  const [backgroundImageFile, setBackgroundImageFile] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allForms, setAllForms] = useState([]);
  const [fullImageModal, setFullImageModal] = useState({ open: false, src: null, alt: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    fetchPage();
    fetchForms();
  }, [id]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (waiverImagePreview && waiverImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(waiverImagePreview);
      }
    };
  }, [waiverImagePreview]);

  const fetchForms = async () => {
    try {
      const response = await api.get('/admin/forms');
      setAllForms(response.data.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    }
  };

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/indemnity-landing-pages/${id}`);
      const pageData = response.data.page;
      
      if (pageData) {
        setPage(pageData);
        setTitle(pageData.title || '');
        setSlug(pageData.slug || '');
        setSelectedFormId(pageData.form_id || '');
        setDisclaimerText(pageData.disclaimer_text || '');
        setIsActive(pageData.is_active);
        if (pageData.waiver_image_url) {
          const waiverUrl = `${ASSET_BASE_URL}${pageData.waiver_image_url}`;
          setWaiverImagePreview(waiverUrl);
          // Extract filename from URL if it's a PDF
          if (pageData.waiver_image_url.toLowerCase().endsWith('.pdf')) {
            const urlParts = pageData.waiver_image_url.split('/');
            setWaiverFileName(urlParts[urlParts.length - 1]);
          }
        }
        if (pageData.background_image_url) {
          setBackgroundImagePreview(`${ASSET_BASE_URL}${pageData.background_image_url}`);
        }
      }
    } catch (error) {
      console.error('Failed to fetch page:', error);
      setErrorMessage('Failed to load indemnity landing page');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleWaiverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage('PDF file size must be less than 10MB');
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
        return;
      }
      
      if (file.type !== 'application/pdf') {
        setErrorMessage('Please upload a PDF file');
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
        return;
      }
      
      // Clean up old object URL if it exists
      if (waiverImagePreview && waiverImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(waiverImagePreview);
      }
      
      setWaiverImageFile(file);
      setWaiverFileName(file.name);
      
      // Create object URL for preview/viewing
      const objectUrl = URL.createObjectURL(file);
      setWaiverImagePreview(objectUrl);
    }
  };

  const handleDeleteWaiverImage = async () => {
    const result = await Swal.fire({
      title: 'Delete Waiver PDF?',
      text: 'Are you sure you want to delete this waiver PDF?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        if (page?.id) {
          await api.delete(`/admin/indemnity-landing-pages/${page.id}/waiver-image`);
        }
        
        setWaiverImageFile(null);
        setWaiverImagePreview(null);
        setWaiverFileName(null);
        
        // Clean up object URL if it exists
        if (waiverImagePreview && waiverImagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(waiverImagePreview);
        }
        
        setSuccessMessage('Waiver PDF deleted successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        
        fetchPage();
      } catch (error) {
        console.error('Failed to delete waiver PDF:', error);
        setErrorMessage('Failed to delete waiver PDF');
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  };

  const handleBackgroundImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('Image file size must be less than 5MB');
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
        return;
      }
      
      setBackgroundImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBackgroundImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteBackgroundImage = async () => {
    const result = await Swal.fire({
      title: 'Delete Background Image?',
      text: 'Are you sure you want to delete this background image?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        if (page?.id) {
          await api.delete(`/admin/indemnity-landing-pages/${page.id}/background-image`);
        }
        
        setBackgroundImageFile(null);
        setBackgroundImagePreview(null);
        
        setSuccessMessage('Background image deleted successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        
        fetchPage();
      } catch (error) {
        console.error('Failed to delete background image:', error);
        setErrorMessage('Failed to delete background image');
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  };

  const handleSave = async () => {
    if (!title || !slug) {
      setErrorMessage('Title and slug are required');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('title', title);
      formData.append('slug', slug);
      formData.append('formId', selectedFormId || '');
      formData.append('disclaimerText', disclaimerText || '');
      formData.append('isActive', isActive ? 'true' : 'false');
      
      if (waiverImageFile) {
        formData.append('waiverImage', waiverImageFile);
      }
      
      if (backgroundImageFile) {
        formData.append('backgroundImage', backgroundImageFile);
      }
      
      const response = await api.put(
        `/admin/indemnity-landing-pages/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setPage(response.data.page);
      setWaiverImageFile(null);
      setBackgroundImageFile(null);
      
      setSuccessMessage('Indemnity landing page saved successfully!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
      fetchPage();
    } catch (error) {
      console.error('Failed to save page:', error);
      setErrorMessage('Failed to save page: ' + (error.response?.data?.error || error.message));
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Indemnity Landing Page?',
      text: 'Are you sure you want to delete this indemnity landing page? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/indemnity-landing-pages/${id}`);
        
        setSuccessMessage('Indemnity landing page deleted successfully!');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          navigate('/admin/indemnity-landing-pages');
        }, 2000);
      } catch (error) {
        console.error('Failed to delete page:', error);
        setErrorMessage('Failed to delete page');
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  };

  const getPageUrl = () => {
    const baseUrl = process.env.REACT_APP_HOSTNAME || window.location.origin;
    return `${baseUrl}/${slug}/indemnity-form`;
  };

  if (loading) {
    return (
      <AdminLayout activeSection="INDEMNITY_LANDING">
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="INDEMNITY_LANDING">
      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error notification */}
      {showError && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{errorMessage}</span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/indemnity-landing-pages')}
              className="text-white hover:text-gray-200"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Edit Indemnity Landing Page</h1>
            </div>
          </div>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center space-x-2"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 space-y-6">
          {/* Title and Slug */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Indemnity Form"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="indemnity-form"
              />
              <p className="text-xs text-gray-500 mt-1">URL-friendly identifier</p>
            </div>
          </div>

          {/* Public URL */}
          {slug && (
            <div className={`border rounded-xl p-4 ${isActive ? 'bg-blue-50 border-blue-200' : 'bg-yellow-50 border-yellow-200'}`}>
              <p className={`text-sm font-medium mb-2 ${isActive ? 'text-blue-900' : 'text-yellow-900'}`}>
                Public Page URL:
                {!isActive ? (
                  <span className="ml-2 text-xs font-normal text-yellow-700">
                    (Page is inactive - activate to make it publicly accessible)
                  </span>
                ) : null}
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={getPageUrl()}
                  readOnly
                  className={`flex-1 px-3 py-2 bg-white border rounded-lg text-sm ${
                    isActive ? 'border-blue-300' : 'border-yellow-300'
                  }`}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(getPageUrl());
                    setSuccessMessage('URL copied to clipboard!');
                    setShowSuccess(true);
                    setTimeout(() => setShowSuccess(false), 2000);
                  }}
                  className={`px-4 py-2 text-white rounded-lg text-sm ${
                    isActive 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  }`}
                >
                  Copy
                </button>
                {isActive ? (
                  <a
                    href={getPageUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                  >
                    Open
                  </a>
                ) : null}
              </div>
            </div>
          )}

          {/* Form Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Form <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Choose which form to display in the indemnity landing page. Create forms from the Forms management page.
            </p>
            <select
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">-- Select a form --</option>
              {allForms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.form_name} {form.campaign_name ? `(${form.campaign_name})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Background Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Image
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Upload a background image for the public indemnity landing page
            </p>
            
            {backgroundImagePreview ? (
              <div className="relative inline-block">
                <div 
                  className="w-48 h-48 rounded-xl border-2 border-gray-300 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setFullImageModal({ open: true, src: backgroundImagePreview, alt: 'Background Image' })}
                >
                  <img
                    src={backgroundImagePreview}
                    alt="Background"
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteBackgroundImage();
                  }}
                  className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 z-10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-teal-600 transition-colors">
                <label className="cursor-pointer">
                  <CloudUpload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                  <span className="text-sm text-gray-600">
                    {uploadingBackground ? 'Uploading...' : 'Click to upload background image'}
                  </span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleBackgroundImageChange}
                    disabled={uploadingBackground}
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">JPG, PNG, GIF, WEBP up to 5MB</p>
              </div>
            )}
          </div>

          {/* Waiver PDF Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Waiver PDF
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Upload a PDF document that will be displayed above the checkboxes (e.g., waiver terms, rules document)
            </p>
            
            {waiverImagePreview ? (
              <div className="border-2 border-gray-300 rounded-xl p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-10 w-10 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {waiverFileName || 'Waiver Document'}
                      </p>
                      <p className="text-xs text-gray-500">PDF Document</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <a
                      href={waiverImagePreview}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      View PDF
                    </a>
                    <button
                      onClick={handleDeleteWaiverImage}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-teal-600 transition-colors">
                <label className="cursor-pointer">
                  <CloudUpload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                  <span className="text-sm text-gray-600">
                    {uploadingWaiver ? 'Uploading...' : 'Click to upload waiver PDF'}
                  </span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="application/pdf"
                    onChange={handleWaiverImageChange}
                    disabled={uploadingWaiver}
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">PDF up to 10MB</p>
              </div>
            )}
          </div>

          {/* Disclaimer Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Disclaimer Text
            </label>
            <p className="text-xs text-gray-500 mb-2">
              This disclaimer will be displayed at the bottom of the indemnity landing page. Use the editor to format your text.
            </p>
            <WysiwygEditor
              value={disclaimerText}
              onChange={(html) => setDisclaimerText(html)}
              placeholder="Enter disclaimer text here..."
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="page-active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <label htmlFor="page-active" className="text-sm font-medium text-gray-700">
              Active (Make indemnity landing page publicly accessible)
            </label>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSave}
              disabled={saving || !title || !slug || !selectedFormId}
              className="px-8 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {/* Info Note */}
          {(!selectedFormId || !title || !slug) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Title, slug, and form selection are required before saving.
              </p>
            </div>
          )}
        </div>

        {/* Full Image Modal */}
        {fullImageModal.open && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setFullImageModal({ open: false, src: null, alt: '' })}
          >
            <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFullImageModal({ open: false, src: null, alt: '' });
                }}
                className="absolute top-4 right-4 text-white hover:text-gray-300 text-4xl font-bold bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-70 transition-all z-10"
                aria-label="Close"
              >
                ×
              </button>
              <img
                src={fullImageModal.src}
                alt={fullImageModal.alt}
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default IndemnityLandingPageEdit;

